/**
 * Shared customer-facing warranty card renderer.
 * Used by staff draft preview, Admin approval review, and issued CLARITY1 view.
 */
import { useMemo, useState } from "react";
import {
  CalendarDays,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  Headset,
  Lock,
  Package,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import ClarityLogo from "@/assets/clarity-logo-light.svg";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatWarrantyDurationLabel, WARRANTY_SALES_TERMS_STATEMENT, WARRANTY_SALES_TERMS_URL } from "@shared/warrantyDomain";
import WarrantyDownloadButton from "./WarrantyDownloadButton";
import { resolveManufacturerWarrantyDisplayLabel } from "@shared/warrantyPeriodAuthority";
import {
  concealContactNumber,
  concealCustomerName,
} from "@/components/warranty/warrantyCustomerPrivacy";

export type WarrantyCardProductView = {
  id: number | string;
  brand: string;
  model: string;
  productName?: string | null;
  finish?: string | null;
  serialNumber?: string | null;
  serialNotApplicable?: boolean;
  warrantyStartDate?: Date | string | null;
  warrantyExpiryDate?: Date | string | null;
  warrantyDurationMonths?: number | null;
  warrantyPeriodSource?: string | null;
  manufacturerWarrantyLabel?: string | null;
  manufacturerTermsUrl?: string | null;
  imageUrl?: string | null;
  imageAlt?: string | null;
  coverageStatus?: "active" | "expiring_soon" | "expired" | "unknown" | "draft" | "review_required";
  durationLabel?: string | null;
  validationWarnings?: string[];
};

export type WarrantyCardCustomerView = {
  name: string;
  contact?: string | null;
  email?: string | null;
  maskPII?: boolean;
};

export type WarrantyCardPresentationProps = {
  products: WarrantyCardProductView[];
  customer: WarrantyCardCustomerView;
  mode: "draft_preview" | "admin_review" | "issued";
  showLoginCta?: boolean;
  /** Official Clarity logo in the document chrome (staff Preview / Admin Review only). */
  showDocumentLogo?: boolean;
  /**
   * Client-only conceal/reveal toggles for name and contact.
   * Pass only from authenticated CLARITY1 — never infer auth here.
   */
  allowCustomerPrivacyToggle?: boolean;
  onContactSupport?: () => void;
  onLogin?: () => void;
  downloadAccess?: "staff" | "customer";
  /** PDF renders the same cards with visible terms and no interactive controls. */
  documentMode?: boolean;
  terms?: {
    claritySupportExplanation: string;
    nonTransferability: string;
    manufacturerAuthority: string;
    mandatoryRightsNote: string;
  };
};

function formatDate(value: Date | string | null | undefined): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "—";
  return date.toLocaleDateString("en-SG", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function maskContact(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw) return "—";
  if (raw.length <= 4) return "••••";
  return `•••• •••• ${raw.slice(-4)}`;
}

function maskEmail(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw || !raw.includes("@")) return raw ? "t*****@*****" : "—";
  const [local, domain] = raw.split("@");
  return `${(local ?? "t").slice(0, 1)}*****@${domain}`;
}

function statusPresentation(status: WarrantyCardProductView["coverageStatus"]) {
  if (status === "review_required") return {
    label: "Review required", className: "text-[#8B7355]",
    detail: "Please contact Clarity to confirm your warranty coverage.",
  };
  if (status === "active") {
    return {
      label: "Active",
      className: "text-[#2E7D32]",
      detail: "Your product is covered under warranty.",
    };
  }
  if (status === "expiring_soon") {
    return {
      label: "Expiring soon",
      className: "text-[#8B7355]",
      detail: "Coverage is approaching its end date.",
    };
  }
  if (status === "expired") {
    return {
      label: "Expired",
      className: "text-[#6B7280]",
      detail: "This warranty period has ended.",
    };
  }
  if (status === "draft") {
    return {
      label: "Draft",
      className: "text-[#1A2744]",
      detail: "Preview only — not yet issued to the customer.",
    };
  }
  return {
    label: "Coverage pending",
    className: "text-[#1A2744]",
    detail: "Coverage details will appear once issued.",
  };
}

function ProductImage({ product }: { product: WarrantyCardProductView }) {
  const [failed, setFailed] = useState(false);
  if (!product.imageUrl || failed) {
    return (
      <div className="flex min-h-56 w-full items-center justify-center bg-white lg:min-h-[22rem]">
        <div className="text-center text-[#8B7355]">
          <Package className="mx-auto mb-2 h-10 w-10" strokeWidth={1.2} />
          <p className="text-[10px] uppercase tracking-[0.16em]">Product image</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-h-56 w-full items-center justify-center bg-white p-6 lg:min-h-[22rem]">
      <img
        src={product.imageUrl}
        alt={product.imageAlt || `${product.brand} ${product.productName || product.model}`}
        className="max-h-72 w-full object-contain lg:max-h-[20rem]"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function IssuedActiveStatusIcon() {
  return (
    <span
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-[#2E7D32] text-[#2E7D32]"
      role="img"
      aria-label="Active warranty"
      data-testid="warranty-issued-active-check"
    >
      <Check className="h-7 w-7" strokeWidth={2.75} aria-hidden />
    </span>
  );
}

function ProductCard({
  product,
  mode,
  terms,
  downloadAccess,
  documentMode,
}: {
  product: WarrantyCardProductView;
  mode: WarrantyCardPresentationProps["mode"];
  terms?: WarrantyCardPresentationProps["terms"];
  downloadAccess?: WarrantyCardPresentationProps["downloadAccess"];
  documentMode?: boolean;
}) {
  const [termsOpen, setTermsOpen] = useState(false);
  const coverageStatus =
    product.coverageStatus ?? (mode === "issued" ? "unknown" : "draft");
  const status = statusPresentation(coverageStatus);
  const showIssuedActiveCheck = mode === "issued" && coverageStatus === "active";
  const duration =
    product.durationLabel ||
    (product.warrantyDurationMonths
      ? formatWarrantyDurationLabel(product.warrantyDurationMonths)
      : null);
  const productTitle = (product.productName || product.model || "Product").trim();
  const warrantyTypeLabel = resolveManufacturerWarrantyDisplayLabel({
    storedLabel: product.manufacturerWarrantyLabel,
    months: product.warrantyDurationMonths,
    source: product.warrantyPeriodSource,
  });
  const serial =
    product.serialNotApplicable
      ? "Not applicable"
      : (product.serialNumber ?? "").trim() || (mode === "issued" ? "—" : "Pending entry");

  return (
    <section className="space-y-4">
      {(product.validationWarnings?.length ?? 0) > 0 ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">Required before submission / issuance</p>
          <ul className="mt-1 list-disc pl-5">
            {product.validationWarnings!.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        className="overflow-hidden rounded-2xl border border-[#E8E4DC] bg-white shadow-sm"
        data-testid="warranty-product-unit"
      >
        <div className="grid gap-0 lg:grid-cols-[minmax(18rem,0.8fr)_minmax(0,1.6fr)] lg:min-h-[22rem]">
          <div className="border-b border-[#E8E4DC] bg-white lg:border-b-0 lg:border-r lg:border-[#E8E4DC]">
            <ProductImage product={product} />
          </div>
          <div className="flex flex-col justify-center space-y-4 p-5 sm:p-6 lg:p-8">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#1A2744]">
                {product.brand}
              </p>
              <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#1A2744] sm:text-2xl lg:text-[1.75rem] lg:leading-tight">
                {productTitle}
              </h2>
            </div>
            <dl className="divide-y divide-[#E8E4DC] text-sm">
              {[
                ["Brand", product.brand],
                ["Model", product.model],
                ...(product.finish ? [["Finish", product.finish] as const] : []),
                ["Serial Number", serial],
                ["Installation Date", formatDate(product.warrantyStartDate)],
                [
                  "Warranty Type",
                  warrantyTypeLabel,
                ],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:items-start sm:justify-between sm:gap-6"
                >
                  <dt className="shrink-0 text-[#8B7355]">{label}</dt>
                  <dd className="font-medium text-[#1A2744] sm:text-right">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <div className="border-t border-[#E8E4DC] p-5 sm:p-6">
          <div className="grid gap-5 sm:grid-cols-3 sm:divide-x sm:divide-[#E8E4DC]">
            <div className="sm:pr-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8B7355]">
                Warranty status
              </p>
              {showIssuedActiveCheck ? (
                <div className="mt-2 flex items-center gap-3">
                  <IssuedActiveStatusIcon />
                  <div className="min-w-0">
                    <div className={`text-lg font-semibold ${status.className}`}>{status.label}</div>
                    <p className="mt-0.5 text-sm text-[#8B7355]">{status.detail}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className={`mt-2 flex items-center gap-2 text-lg font-semibold ${status.className}`}>
                    <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />
                    {status.label}
                  </div>
                  <p className="mt-1 text-sm text-[#8B7355]">{status.detail}</p>
                </>
              )}
            </div>
            <div className="sm:px-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8B7355]">
                Warranty start date
              </p>
              <div className="mt-2 flex items-center gap-2 text-[#1A2744]">
                <CalendarDays className="h-4 w-4 shrink-0 text-[#B8956E]" />
                <span className="font-medium">{formatDate(product.warrantyStartDate)}</span>
              </div>
            </div>
            <div className="sm:pl-5">
              <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8B7355]">
                Warranty end date
              </p>
              <div className="mt-2 flex items-center gap-2 text-[#1A2744]">
                <CalendarDays className="h-4 w-4 shrink-0 text-[#B8956E]" />
                <span className="font-medium">{formatDate(product.warrantyExpiryDate)}</span>
              </div>
              {duration ? <p className="mt-1 text-sm text-[#8B7355]">({duration})</p> : null}
            </div>
          </div>
          {!documentMode ? (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {downloadAccess && mode === "issued" ? (
                <WarrantyDownloadButton itemId={Number(product.id)} access={downloadAccess} />
              ) : null}
              <button
                type="button"
                className="rounded-full bg-[#1A2744] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#243556]"
                onClick={() => setTermsOpen(true)}
              >
                View Warranty Terms
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-lg border-[#E8E4DC] bg-[#F9F7F2]">
          <DialogHeader>
            <DialogTitle className="text-[#1A2744]">Manufacturer Warranty Terms</DialogTitle>
            <DialogDescription className="text-[#8B7355]">
              {product.brand} {productTitle}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-[#1A2744]">
            <p>
              <span className="font-medium">Coverage label:</span>{" "}
              {warrantyTypeLabel}
            </p>
            <p>
              <span className="font-medium">Period:</span>{" "}
              {formatDate(product.warrantyStartDate)} – {formatDate(product.warrantyExpiryDate)}
              {duration ? ` (${duration})` : ""}
            </p>
            {terms ? (
              <>
                <p>{terms.claritySupportExplanation}</p>
                <p className="rounded-lg border border-[#E8E4DC] bg-white p-3">
                  {terms.nonTransferability}
                </p>
                <p>{terms.manufacturerAuthority}</p>
                <p>{WARRANTY_SALES_TERMS_STATEMENT} <a href={WARRANTY_SALES_TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline">Read full terms and conditions of sale</a></p>
                <p className="text-[#8B7355]">{terms.mandatoryRightsNote}</p>
              </>
            ) : null}
            {product.manufacturerTermsUrl ? (
              <a
                href={product.manufacturerTermsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block text-[#B8956E] underline"
              >
                Open manufacturer terms
              </a>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function PrivacyToggleButton({
  pressed,
  hideLabel,
  showLabel,
  onToggle,
}: {
  pressed: boolean;
  hideLabel: string;
  showLabel: string;
  onToggle: () => void;
}) {
  const label = pressed ? showLabel : hideLabel;
  const Icon = pressed ? EyeOff : Eye;
  return (
    <button
      type="button"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[#8B7355] transition hover:bg-[#F0EDE6] hover:text-[#1A2744] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B8956E] focus-visible:ring-offset-2"
      aria-label={label}
      aria-pressed={pressed}
      onClick={onToggle}
      data-testid={hideLabel.toLowerCase().includes("name") ? "privacy-toggle-name" : "privacy-toggle-contact"}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}

export default function WarrantyCardPresentation({
  products,
  customer,
  mode,
  showLoginCta = mode === "issued",
  showDocumentLogo = false,
  allowCustomerPrivacyToggle = false,
  onContactSupport,
  onLogin,
  terms,
  downloadAccess,
  documentMode = false,
}: WarrantyCardPresentationProps) {
  const included = useMemo(() => products.filter(Boolean), [products]);
  const [nameConcealed, setNameConcealed] = useState(false);
  const [contactConcealed, setContactConcealed] = useState(false);

  const privacyEnabled = allowCustomerPrivacyToggle && customer.maskPII === false;
  const rawName = customer.name || "—";
  const rawContact = customer.contact || "—";
  const displayName =
    privacyEnabled && nameConcealed ? concealCustomerName(rawName) : rawName;
  const displayContact = !privacyEnabled
    ? customer.maskPII !== false
      ? maskContact(customer.contact)
      : rawContact
    : contactConcealed
      ? concealContactNumber(rawContact === "—" ? "" : rawContact) || "—"
      : rawContact;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-6 py-6 lg:px-8">
      {showDocumentLogo ? (
        <div className="flex justify-end">
          <img
            src={ClarityLogo}
            alt="Clarity"
            className="h-7 w-auto object-contain sm:h-8"
            style={{ maxWidth: "10rem" }}
            data-testid="warranty-document-logo"
          />
        </div>
      ) : null}

      {mode === "draft_preview" || mode === "admin_review" ? (
        <div className="rounded-xl border border-[#1A2744]/20 bg-[#1A2744] px-4 py-2 text-center text-xs font-semibold uppercase tracking-[0.14em] text-white">
          {mode === "admin_review"
            ? "Admin review — pending approval"
            : "Draft preview — not issued"}
        </div>
      ) : null}

      <div className="text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-[#B8956E]/40 bg-white text-[#B8956E]">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#1A2744] sm:text-3xl">
          Your Warranty Information
        </h1>
        <p className="mx-auto mt-2 max-w-2xl text-sm text-[#8B7355]">
          Thank you for choosing Clarity Group. Below are the warranty details for your product
          {included.length > 1 ? "s" : ""}.
        </p>
      </div>

      {included.map((product) => (
        <ProductCard key={product.id} product={product} mode={mode} terms={terms} downloadAccess={downloadAccess} documentMode={documentMode} />
      ))}

      <div className="rounded-2xl border border-[#E8E4DC] bg-white p-5 shadow-sm sm:p-6">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <div>
            <div className="mb-3 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8B7355]">
              <UserRound className="h-3.5 w-3.5" />
              Customer information
            </div>
            <dl className="space-y-2 text-sm">
              <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <dt className="text-[#8B7355]">Customer Name</dt>
                <dd className="flex items-center justify-start gap-1.5 font-medium text-[#1A2744] sm:justify-end">
                  <span data-testid="warranty-customer-name">{displayName}</span>
                  {privacyEnabled ? (
                    <PrivacyToggleButton
                      pressed={nameConcealed}
                      hideLabel="Hide customer name"
                      showLabel="Show customer name"
                      onToggle={() => setNameConcealed((v) => !v)}
                    />
                  ) : null}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <dt className="text-[#8B7355]">Contact Number</dt>
                <dd className="flex items-center justify-start gap-1.5 font-medium text-[#1A2744] sm:justify-end">
                  <span data-testid="warranty-customer-contact">{displayContact}</span>
                  {privacyEnabled ? (
                    <PrivacyToggleButton
                      pressed={contactConcealed}
                      hideLabel="Hide contact number"
                      showLabel="Show contact number"
                      onToggle={() => setContactConcealed((v) => !v)}
                    />
                  ) : null}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
                <dt className="text-[#8B7355]">Email Address</dt>
                <dd className="font-medium text-[#1A2744]">
                  {customer.maskPII !== false
                    ? maskEmail(customer.email)
                    : customer.email || "—"}
                </dd>
              </div>
            </dl>
            {showLoginCta ? (
              <p className="mt-3 text-xs text-[#8B7355]">
                Log in to your account to view full details, service history and more.
              </p>
            ) : null}
          </div>
          {showLoginCta ? (
            <div className="rounded-xl bg-[#F0EDE6] p-4">
              <div className="flex items-start gap-2">
                <Lock className="mt-0.5 h-4 w-4 text-[#1A2744]" />
                <div>
                  <p className="text-sm font-semibold text-[#1A2744]">Log in for full access</p>
                  <p className="mt-1 text-xs text-[#8B7355]">
                    View invoices, service history, product documents and manage your devices.
                  </p>
                  <button
                    type="button"
                    onClick={onLogin}
                    className="mt-3 rounded-full bg-[#1A2744] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white"
                  >
                    Log In / Register
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {documentMode && terms ? <section className="space-y-3 rounded-2xl border border-[#E8E4DC] bg-white p-5 text-sm text-[#1A2744] shadow-sm">
        <h2 className="font-semibold">Warranty Terms &amp; Conditions</h2>
        <p>{terms.nonTransferability}</p>
        <p>{terms.claritySupportExplanation}</p>
        <p>{terms.manufacturerAuthority}</p>
        <p className="text-[#8B7355]">{terms.mandatoryRightsNote}</p>
        <p>{WARRANTY_SALES_TERMS_STATEMENT}</p>
        <a href={WARRANTY_SALES_TERMS_URL} className="block text-[#8B7355] underline">Terms &amp; Conditions of Sale</a>
        {included.map(product => product.manufacturerTermsUrl ? <a key={product.id} href={product.manufacturerTermsUrl} className="block text-[#8B7355] underline">Manufacturer warranty terms</a> : null)}
      </section> : null}

      {!documentMode ? <div className="flex flex-col gap-4 rounded-2xl border border-[#E8E4DC] bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div className="flex items-start gap-3">
          <Headset className="mt-0.5 h-5 w-5 shrink-0 text-[#B8956E]" />
          <div>
            <p className="font-semibold text-[#1A2744]">Need Help?</p>
            <p className="text-sm text-[#8B7355]">
              Our team is here to assist you with any questions regarding your product or warranty.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onContactSupport}
          className="shrink-0 rounded-full bg-[#1A2744] px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white"
        >
          Contact Support
        </button>
      </div> : null}

      <p className="flex flex-wrap items-center justify-center gap-2 pb-2 text-center text-xs text-[#8B7355]">
        <ShieldCheck className="h-3.5 w-3.5 text-[#B8956E]" />
        This is an official Clarity Group product. For verification, visit{" "}
        <a href="https://claritygroup.com.sg" className="text-[#B8956E] underline">
          claritygroup.com.sg
        </a>
      </p>
    </div>
  );
}
